/**
 * useVisitorLocation — IP-based, prompt-free guess of where the visitor is.
 *
 * Calls the `geo-locate` edge function once per session and caches the result
 * in sessionStorage. Always resolves (never throws): when detection fails the
 * hook returns `null` and callers silently fall back to the global view.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { keys } from "@/data/keys";

export interface VisitorLocation {
  id: string;
  name: string;
  slug: string;
  type: string;
  country_code: string | null;
}

const CACHE_KEY = "brrgr_visitor_location";

const readCache = (): VisitorLocation | null | undefined => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw === null) return undefined;
    return JSON.parse(raw) as VisitorLocation | null;
  } catch {
    return undefined;
  }
};

const writeCache = (value: VisitorLocation | null) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

export function useVisitorLocation() {
  return useQuery<VisitorLocation | null>({
    queryKey: [...keys.locations.all, "visitor"],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const cached = readCache();
      if (cached !== undefined) return cached;

      try {
        const { data, error } = await supabase.functions.invoke("geo-locate");
        if (error) throw error;
        const location = (data as { location: VisitorLocation | null } | null)?.location ?? null;
        writeCache(location);
        return location;
      } catch {
        writeCache(null);
        return null;
      }
    },
  });
}
